import { useState, useEffect, useCallback, useRef } from 'react';
import type { paths } from '../lib/api-types';
import { sseService, type SSEEvent } from '../services/sse';

type Message =
  paths['/v1/conversations/{conversationId}/messages']['get']['responses']['200']['content']['application/json']['messages'][0];

interface Reaction {
  emoji: string;
  message_id: string;
}

interface ParticipantEvent {
  user_id: string;
  username?: string;
}

export interface UseSSEOptions {
  conversationId: string | null;
  onMessageCreated?: (message: Message) => void;
  onMessageUpdated?: (message: Message) => void;
  onMessageDeleted?: (messageId: string) => void;
  onTypingStarted?: (username: string) => void;
  onTypingEnded?: (username: string) => void;
  onReactionAdded?: (reaction: Reaction) => void;
  onReactionRemoved?: (reaction: Reaction) => void;
  onParticipantAdded?: (participant: ParticipantEvent) => void;
  onParticipantRemoved?: (participant: ParticipantEvent) => void;
}

export interface UseSSEReturn {
  connected: boolean;
  error: Error | null;
  reconnect: () => void;
}

/**
 * SSE connection management hook
 * Uses cookie-based authentication automatically
 *
 * @example
 * ```typescript
 * const { connected } = useSSE({
 *   conversationId,
 *   onMessageCreated: (msg) => {
 *     setMessages(prev => [...prev, msg]);
 *   },
 *   onTypingStarted: (username) => {
 *     setTypingUsers(prev => new Set(prev).add(username));
 *   },
 * });
 * ```
 */
export function useSSE(options: UseSSEOptions): UseSSEReturn {
  const {
    conversationId,
    onMessageCreated,
    onMessageUpdated,
    onMessageDeleted,
    onTypingStarted,
    onTypingEnded,
    onReactionAdded,
    onReactionRemoved,
    onParticipantAdded,
    onParticipantRemoved,
  } = options;

  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Use refs to avoid re-creating handlers when callbacks change
  const handlersRef = useRef(options);
  handlersRef.current = options;

  const reconnect = useCallback(() => {
    if (conversationId) {
      sseService.connect(conversationId).catch((err) => {
        setError(err instanceof Error ? err : new Error(String(err)));
      });
    }
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId) {
      sseService.disconnect();
      setConnected(false);
      return;
    }

    // Connect to SSE (uses cookie auth automatically)
    sseService
      .connect(conversationId)
      .then(() => {
        setConnected(true);
        setError(null);
      })
      .catch((err) => {
        setError(err instanceof Error ? err : new Error(String(err)));
        setConnected(false);
      });

    // Register event handlers
    const unsubscribers: (() => void)[] = [];

    if (onMessageCreated) {
      unsubscribers.push(
        sseService.on('message.created', (event: SSEEvent) => {
          handlersRef.current.onMessageCreated?.(event.data as Message);
        })
      );
    }

    if (onMessageUpdated) {
      unsubscribers.push(
        sseService.on('message.updated', (event: SSEEvent) => {
          handlersRef.current.onMessageUpdated?.(event.data as Message);
        })
      );
    }

    if (onMessageDeleted) {
      unsubscribers.push(
        sseService.on('message.deleted', (event: SSEEvent) => {
          const data = event.data as { id: string };
          handlersRef.current.onMessageDeleted?.(data.id);
        })
      );
    }

    if (onTypingStarted) {
      unsubscribers.push(
        sseService.on('typing.started', (event: SSEEvent) => {
          const data = event.data as { username: string };
          handlersRef.current.onTypingStarted?.(data.username);
        })
      );
    }

    if (onTypingEnded) {
      unsubscribers.push(
        sseService.on('typing.stopped', (event: SSEEvent) => {
          const data = event.data as { username: string };
          handlersRef.current.onTypingEnded?.(data.username);
        })
      );
    }

    if (onReactionAdded) {
      unsubscribers.push(
        sseService.on('reaction.added', (event: SSEEvent) => {
          handlersRef.current.onReactionAdded?.(event.data as Reaction);
        })
      );
    }

    if (onReactionRemoved) {
      unsubscribers.push(
        sseService.on('reaction.removed', (event: SSEEvent) => {
          handlersRef.current.onReactionRemoved?.(event.data as Reaction);
        })
      );
    }

    if (onParticipantAdded) {
      unsubscribers.push(
        sseService.on('participant.added', (event: SSEEvent) => {
          handlersRef.current.onParticipantAdded?.(event.data as ParticipantEvent);
        })
      );
    }

    if (onParticipantRemoved) {
      unsubscribers.push(
        sseService.on('participant.removed', (event: SSEEvent) => {
          handlersRef.current.onParticipantRemoved?.(event.data as ParticipantEvent);
        })
      );
    }

    // Check connection status periodically
    const checkInterval = setInterval(() => {
      setConnected(sseService.isConnected());
    }, 5000);

    // Cleanup
    return () => {
      clearInterval(checkInterval);
      unsubscribers.forEach((unsub) => unsub());
      sseService.disconnect();
      setConnected(false);
    };
    // Only depend on conversationId - callbacks are accessed via handlersRef
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  return { connected, error, reconnect };
}
